type DirtyAtom = Date | File | Blob | RegExp;

type Identifier = string | number;

interface DirtyRecord {
  readonly [key: string]: DirtyValue;
}

type DirtyValue =
  | string
  | number
  | boolean
  | bigint
  | symbol
  | null
  | undefined
  | DirtyAtom
  | DirtyRecord
  | readonly DirtyValue[];

type DirtyFields<TValue, TIdentity extends string> = {
  [TKey in keyof TValue]?: DirtyValues<TValue[TKey], TIdentity>;
};

type DirtyObject<TValue, TIdentity extends string> = [TIdentity] extends [never]
  ? DirtyFields<TValue, TIdentity>
  : TValue extends Record<TIdentity, Identifier>
    ? DirtyFields<TValue, TIdentity> & Pick<TValue, TIdentity & keyof TValue>
    : DirtyFields<TValue, TIdentity>;

type DirtyItem<TItem, TIdentity extends string> = [TIdentity] extends [never]
  ? TItem
  : TItem extends Record<TIdentity, Identifier>
    ? DirtyValues<TItem, TIdentity>
    : TItem;

export type DirtyValues<TValue, TIdentity extends string = never> = TValue extends DirtyAtom
  ? TValue
  : TValue extends ReadonlyArray<infer TItem>
    ? DirtyItem<TItem, TIdentity>[]
    : TValue extends object
      ? DirtyObject<TValue, TIdentity>
      : TValue;

export interface DirtyValuesOptions<TIdentity extends string> {
  identity?: TIdentity;
}

const UNCHANGED = Symbol("unchanged");

type Unchanged = typeof UNCHANGED;

const isRecord = (value: DirtyValue): value is DirtyRecord => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const isList = (value: DirtyValue): value is readonly DirtyValue[] => Array.isArray(value);

const isValueObject = (value: object): value is { valueOf: () => Identifier | boolean | bigint } => {
  const primitive = value.valueOf();
  return (
    typeof primitive === "string" ||
    typeof primitive === "number" ||
    typeof primitive === "boolean" ||
    typeof primitive === "bigint"
  );
};

const isSameLeaf = (defaultValue: DirtyValue, value: DirtyValue): boolean => {
  if (Object.is(defaultValue, value)) return true;
  if (typeof defaultValue !== "object" || defaultValue === null) return false;
  if (typeof value !== "object" || value === null) return false;
  return isValueObject(defaultValue) && isValueObject(value) && Object.is(defaultValue.valueOf(), value.valueOf());
};

const indexByIdentity = (list: readonly DirtyValue[], identity: string): Map<Identifier, DirtyRecord> | undefined => {
  const index = new Map<Identifier, DirtyRecord>();
  for (const item of list) {
    if (!isRecord(item)) return undefined;
    const id = item[identity];
    if ((typeof id !== "string" && typeof id !== "number") || index.has(id)) return undefined;
    index.set(id, item);
  }
  return index;
};

const diffIdentifiedItems = (
  defaultsById: Map<Identifier, DirtyRecord>,
  itemsById: Map<Identifier, DirtyRecord>,
  identity: string,
): DirtyRecord[] | Unchanged | undefined => {
  if (defaultsById.size !== itemsById.size) return undefined;
  const dirtyItems: DirtyRecord[] = [];
  for (const [id, item] of itemsById) {
    const defaultItem = defaultsById.get(id);
    if (!defaultItem) return undefined;
    const itemDiff = diffRecord(defaultItem, item, identity);
    if (itemDiff !== UNCHANGED) dirtyItems.push(itemDiff);
  }
  return dirtyItems.length > 0 ? dirtyItems : UNCHANGED;
};

const diffList = (
  defaultList: readonly DirtyValue[],
  list: readonly DirtyValue[],
  identity: string | undefined,
): readonly DirtyValue[] | Unchanged => {
  if (Object.is(defaultList, list)) return UNCHANGED;
  if (identity !== undefined) {
    const defaultsById = indexByIdentity(defaultList, identity);
    const itemsById = indexByIdentity(list, identity);
    if (defaultsById && itemsById) {
      const identifiedDiff = diffIdentifiedItems(defaultsById, itemsById, identity);
      if (identifiedDiff) return identifiedDiff;
    }
  }
  if (defaultList.length !== list.length) return list;
  return list.some((item, index) => diff(defaultList[index], item, identity) !== UNCHANGED) ? list : UNCHANGED;
};

const diffRecord = (
  defaultRecord: DirtyRecord,
  record: DirtyRecord,
  identity: string | undefined,
): DirtyRecord | Unchanged => {
  if (Object.is(defaultRecord, record)) return UNCHANGED;
  const dirtyRecord: Record<string, DirtyValue> = {};
  if (identity !== undefined) {
    const id = record[identity];
    if (typeof id === "string" || typeof id === "number") dirtyRecord[identity] = id;
  }
  let hasDirtyField = false;
  for (const [key, value] of Object.entries(record)) {
    const valueDiff = diff(defaultRecord[key], value, identity);
    if (valueDiff === UNCHANGED) continue;
    dirtyRecord[key] = valueDiff;
    hasDirtyField = true;
  }
  return hasDirtyField ? dirtyRecord : UNCHANGED;
};

const diff = (defaultValue: DirtyValue, value: DirtyValue, identity: string | undefined): DirtyValue | Unchanged => {
  if (isRecord(value) && isRecord(defaultValue)) return diffRecord(defaultValue, value, identity);
  if (isList(value) && isList(defaultValue)) return diffList(defaultValue, value, identity);
  return isSameLeaf(defaultValue, value) ? UNCHANGED : value;
};

export function getDirtyValues<TValue, TIdentity extends string = never>(
  defaultValues: TValue | undefined,
  values: TValue,
  options?: DirtyValuesOptions<TIdentity>,
): DirtyValues<TValue, TIdentity> | undefined;
export function getDirtyValues(
  defaultValues: DirtyValue,
  values: DirtyValue,
  options?: DirtyValuesOptions<string>,
): DirtyValue {
  const dirtyValues = diff(defaultValues, values, options?.identity);
  return dirtyValues === UNCHANGED ? undefined : dirtyValues;
}
